import { useTranslation } from '../../../shared/i18n';
import React from 'react';
import { Tag, Upload, Button, message } from 'antd';
import { PaperClipOutlined } from '@ant-design/icons';

/** 工单状态：open=等待客服处理 / answered=客服已回复 / closed=已关闭 */
export const STATUS_META = {
    open: { color: 'processing', label: '处理中', hint: '等待客服处理' },
    answered: { color: 'warning', label: '已回复', hint: '客服已回复，等待你回复' },
    closed: { color: 'default', label: '已关闭', hint: '已关闭' },
};

export const PRIORITY_META = {
    low: { color: 'default', label: '低' },
    normal: { color: 'blue', label: '普通' },
    high: { color: 'orange', label: '高' },
    urgent: { color: 'red', label: '紧急' },
};

/** 与后端 SupportServices::TYPES 对应，列表里只有 service_type 时用于显示 */
export const SERVICE_TYPE_LABELS = {
    evm: '云服务器', cdn: 'CDN', dns: 'DNS', domain: '域名', ssl: 'SSL 证书',
    yeapmail: '企业邮箱', container: '独享容器', host: '应用容器', tm: '邮件推送',
};

export const DEFAULT_LIMITS = { max_files: 5, max_file_mb: 10, extensions: ['png', 'jpg', 'jpeg', 'gif', 'webp', 'pdf', 'txt', 'log', 'zip', 'json', 'csv'] };

export const ok = (r) => r && r.status === 'success';

export const TicketStatusTag = ({ status }) => {
    const { t } = useTranslation();
    const m = STATUS_META[status] || { color: 'default', label: status };
    return <Tag color={m.color} style={{ marginInlineEnd: 0 }}>{t(m.label)}</Tag>;
};

export const PriorityTag = ({ priority }) => {
    const { t } = useTranslation();
    const m = PRIORITY_META[priority];
    if (!m) return null;
    return <Tag color={m.color} style={{ marginInlineEnd: 0 }}>{t(m.label)}</Tag>;
};

export const serviceText = (t, type, label) => {
    if (!type && !label) return null;
    const typeLabel = type ? t(SERVICE_TYPE_LABELS[type] || type) : '';
    return [typeLabel, label].filter(Boolean).join(' · ');
};

export const formatSize = (bytes) => {
    const n = Number(bytes) || 0;
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * 附件选择（文件先留在本地，提交时由浏览器直传对象存储，见 shared/utils/directUpload）。
 * value/onChange: antd UploadFile[]；客户端校验数量/大小/扩展名。
 */
export const AttachmentPicker = ({ value = [], onChange, limits, disabled }) => {
    const { t } = useTranslation();
    const lim = { ...DEFAULT_LIMITS, ...(limits || {}) };
    const exts = (lim.extensions || []).map((e) => String(e).toLowerCase().replace(/^\./, ''));
    const full = value.length >= lim.max_files;

    const beforeUpload = (file, batch) => {
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        if (exts.length && (!file.name.includes('.') || !exts.includes(ext))) {
            message.error(t('Unsupported file type: {{name}}', { name: file.name }));
            return Upload.LIST_IGNORE;
        }
        if (file.size > lim.max_file_mb * 1024 * 1024) {
            message.error(t('File exceeds {{mb}} MB: {{name}}', { mb: lim.max_file_mb, name: file.name }));
            return Upload.LIST_IGNORE;
        }
        // 同批次多选时，按批次内序号判断是否超出上限
        const idx = batch ? batch.indexOf(file) : 0;
        if (value.length + idx + 1 > lim.max_files) {
            message.error({ key: 'support-max-files', content: t('You can attach up to {{n}} files', { n: lim.max_files }) });
            return Upload.LIST_IGNORE;
        }
        return false;
    };

    return (
        <Upload
            multiple
            disabled={disabled}
            fileList={value}
            beforeUpload={beforeUpload}
            onChange={({ fileList }) => onChange && onChange(fileList.slice(0, lim.max_files))}
            accept={exts.map((e) => `.${e}`).join(',')}
        >
            <Button icon={<PaperClipOutlined />} disabled={disabled || full}>
                {t('Attach files')}
            </Button>
            <span style={{ marginInlineStart: 8, fontSize: 12, color: 'var(--text-3, rgba(0,0,0,0.45))' }} onClick={(e) => e.stopPropagation()}>
                {t('Up to {{n}} files, ≤ {{mb}} MB each', { n: lim.max_files, mb: lim.max_file_mb })}
            </span>
        </Upload>
    );
};
